/* __next_internal_client_entry_do_not_use__ default auto */ import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import composeClasses from '@mui/utils/composeClasses';
import { alpha } from '@mui/system/colorManipulator';
import { useRtl } from '@mui/system/RtlProvider';
import paginationItemClasses, { getPaginationItemUtilityClass } from "./paginationItemClasses.js";
import ButtonBase from "../ButtonBase/index.js";
import capitalize from "../utils/capitalize.js";
import createSimplePaletteValueFilter from "../utils/createSimplePaletteValueFilter.js";
import FirstPageIcon from "../internal/svg-icons/FirstPage.js";
import LastPageIcon from "../internal/svg-icons/LastPage.js";
import NavigateBeforeIcon from "../internal/svg-icons/NavigateBefore.js";
import NavigateNextIcon from "../internal/svg-icons/NavigateNext.js";
import useSlot from "../utils/useSlot.js";
import { styled } from "../zero-styled/index.js";
import memoTheme from "../utils/memoTheme.js";
import { useDefaultProps } from "../DefaultPropsProvider/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const overridesResolver = (props, styles)=>{
    const { ownerState } = props;
    return [
        styles.root,
        styles[ownerState.variant],
        styles["size".concat(capitalize(ownerState.size))],
        ownerState.variant === 'text' && styles["text".concat(capitalize(ownerState.color))],
        ownerState.variant === 'outlined' && styles["outlined".concat(capitalize(ownerState.color))],
        ownerState.shape === 'rounded' && styles.rounded,
        ownerState.type === 'page' && styles.page,
        (ownerState.type === 'start-ellipsis' || ownerState.type === 'end-ellipsis') && styles.ellipsis,
        (ownerState.type === 'previous' || ownerState.type === 'next') && styles.previousNext,
        (ownerState.type === 'first' || ownerState.type === 'last') && styles.firstLast
    ];
};
const useUtilityClasses = (ownerState)=>{
    const { classes, color, disabled, selected, size, shape, type, variant } = ownerState;
    const slots = {
        root: [
            'root',
            "size".concat(capitalize(size)),
            variant,
            shape,
            color !== 'standard' && "color".concat(capitalize(color)),
            color !== 'standard' && "".concat(variant).concat(capitalize(color)),
            disabled && 'disabled',
            selected && 'selected',
            {
                page: 'page',
                first: 'firstLast',
                last: 'firstLast',
                'start-ellipsis': 'ellipsis',
                'end-ellipsis': 'ellipsis',
                previous: 'previousNext',
                next: 'previousNext'
            }[type]
        ],
        icon: [
            'icon'
        ]
    };
    return composeClasses(slots, getPaginationItemUtilityClass, classes);
};
const PaginationItemEllipsis = styled('div', {
    name: 'MuiPaginationItem',
    slot: 'Root',
    overridesResolver
})(memoTheme((param)=>{
    let { theme } = param;
    return {
        ...theme.typography.body2,
        borderRadius: 32 / 2,
        textAlign: 'center',
        boxSizing: 'border-box',
        minWidth: 32,
        padding: '0 6px',
        margin: '0 3px',
        color: (theme.vars || theme).palette.text.primary,
        height: 'auto',
        ["&.".concat(paginationItemClasses.disabled)]: {
            opacity: (theme.vars || theme).palette.action.disabledOpacity
        },
        variants: [
            {
                props: {
                    size: 'small'
                },
                style: {
                    minWidth: 26,
                    borderRadius: 26 / 2,
                    margin: '0 1px',
                    padding: '0 4px'
                }
            },
            {
                props: {
                    size: 'large'
                },
                style: {
                    minWidth: 40,
                    borderRadius: 40 / 2,
                    padding: '0 10px',
                    fontSize: theme.typography.pxToRem(15)
                }
            }
        ]
    };
}));
const PaginationItemPage = styled(ButtonBase, {
    name: 'MuiPaginationItem',
    slot: 'Root',
    overridesResolver
})(memoTheme((param)=>{
    let { theme } = param;
    return {
        ...theme.typography.body2,
        borderRadius: 32 / 2,
        textAlign: 'center',
        boxSizing: 'border-box',
        minWidth: 32,
        height: 32,
        padding: '0 6px',
        margin: '0 3px',
        color: (theme.vars || theme).palette.text.primary,
        ["&.".concat(paginationItemClasses.focusVisible)]: {
            backgroundColor: (theme.vars || theme).palette.action.focus
        },
        ["&.".concat(paginationItemClasses.disabled)]: {
            opacity: (theme.vars || theme).palette.action.disabledOpacity
        },
        transition: theme.transitions.create([
            'color',
            'background-color'
        ], {
            duration: theme.transitions.duration.short
        }),
        '&:hover': {
            backgroundColor: (theme.vars || theme).palette.action.hover,
            // Reset on touch devices, it doesn't add specificity
            '@media (hover: none)': {
                backgroundColor: 'transparent'
            }
        },
        ["&.".concat(paginationItemClasses.selected)]: {
            backgroundColor: (theme.vars || theme).palette.action.selected,
            '&:hover': {
                backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette.action.selectedChannel, " / calc(").concat(theme.vars.palette.action.selectedOpacity, " + ").concat(theme.vars.palette.action.hoverOpacity, "))") : alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: (theme.vars || theme).palette.action.selected
                }
            },
            ["&.".concat(paginationItemClasses.focusVisible)]: {
                backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette.action.selectedChannel, " / calc(").concat(theme.vars.palette.action.selectedOpacity, " + ").concat(theme.vars.palette.action.focusOpacity, "))") : alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity + theme.palette.action.focusOpacity)
            },
            ["&.".concat(paginationItemClasses.disabled)]: {
                opacity: 1,
                color: (theme.vars || theme).palette.action.disabled,
                backgroundColor: (theme.vars || theme).palette.action.selected
            }
        },
        variants: [
            {
                props: {
                    size: 'small'
                },
                style: {
                    minWidth: 26,
                    height: 26,
                    borderRadius: 26 / 2,
                    margin: '0 1px',
                    padding: '0 4px'
                }
            },
            {
                props: {
                    size: 'large'
                },
                style: {
                    minWidth: 40,
                    height: 40,
                    borderRadius: 40 / 2,
                    padding: '0 10px',
                    fontSize: theme.typography.pxToRem(15)
                }
            },
            {
                props: {
                    shape: 'rounded'
                },
                style: {
                    borderRadius: (theme.vars || theme).shape.borderRadius
                }
            },
            {
                props: {
                    variant: 'outlined'
                },
                style: {
                    border: theme.vars ? "1px solid rgba(".concat(theme.vars.palette.common.onBackgroundChannel, " / 0.23)") : "1px solid ".concat(theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'),
                    ["&.".concat(paginationItemClasses.selected)]: {
                        ["&.".concat(paginationItemClasses.disabled)]: {
                            borderColor: (theme.vars || theme).palette.action.disabledBackground,
                            color: (theme.vars || theme).palette.action.disabled
                        }
                    }
                }
            },
            {
                props: {
                    variant: 'text'
                },
                style: {
                    ["&.".concat(paginationItemClasses.selected)]: {
                        ["&.".concat(paginationItemClasses.disabled)]: {
                            color: (theme.vars || theme).palette.action.disabled
                        }
                    }
                }
            },
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter([
                'dark',
                'contrastText'
            ])).map((param)=>{
                let [color] = param;
                return {
                    props: {
                        variant: 'text',
                        color
                    },
                    style: {
                        ["&.".concat(paginationItemClasses.selected)]: {
                            color: (theme.vars || theme).palette[color].contrastText,
                            backgroundColor: (theme.vars || theme).palette[color].main,
                            '&:hover': {
                                backgroundColor: (theme.vars || theme).palette[color].dark,
                                // Reset on touch devices, it doesn't add specificity
                                '@media (hover: none)': {
                                    backgroundColor: (theme.vars || theme).palette[color].main
                                }
                            },
                            ["&.".concat(paginationItemClasses.focusVisible)]: {
                                backgroundColor: (theme.vars || theme).palette[color].dark
                            },
                            ["&.".concat(paginationItemClasses.disabled)]: {
                                color: (theme.vars || theme).palette.action.disabled
                            }
                        }
                    }
                };
            }),
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter([
                'light'
            ])).map((param)=>{
                let [color] = param;
                return {
                    props: {
                        variant: 'outlined',
                        color
                    },
                    style: {
                        ["&.".concat(paginationItemClasses.selected)]: {
                            color: (theme.vars || theme).palette[color].main,
                            border: "1px solid ".concat(theme.vars ? "rgba(".concat(theme.vars.palette[color].mainChannel, " / 0.5)") : alpha(theme.palette[color].main, 0.5)),
                            backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette[color].mainChannel, " / ").concat(theme.vars.palette.action.activatedOpacity, ")") : alpha(theme.palette[color].main, theme.palette.action.activatedOpacity),
                            '&:hover': {
                                backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette[color].mainChannel, " / calc(").concat(theme.vars.palette.action.activatedOpacity, " + ").concat(theme.vars.palette.action.focusOpacity, "))") : alpha(theme.palette[color].main, theme.palette.action.activatedOpacity + theme.palette.action.focusOpacity),
                                // Reset on touch devices, it doesn't add specificity
                                '@media (hover: none)': {
                                    backgroundColor: 'transparent'
                                }
                            },
                            ["&.".concat(paginationItemClasses.focusVisible)]: {
                                backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette[color].mainChannel, " / calc(").concat(theme.vars.palette.action.activatedOpacity, " + ").concat(theme.vars.palette.action.focusOpacity, "))") : alpha(theme.palette[color].main, theme.palette.action.activatedOpacity + theme.palette.action.focusOpacity)
                            }
                        }
                    }
                };
            })
        ]
    };
}));
const PaginationItemPageIcon = styled('div', {
    name: 'MuiPaginationItem',
    slot: 'Icon',
    overridesResolver: (props, styles)=>styles.icon
})(memoTheme((param)=>{
    let { theme } = param;
    return {
        fontSize: theme.typography.pxToRem(20),
        margin: '0 -8px',
        variants: [
            {
                props: {
                    size: 'small'
                },
                style: {
                    fontSize: theme.typography.pxToRem(18)
                }
            },
            {
                props: {
                    size: 'large'
                },
                style: {
                    fontSize: theme.typography.pxToRem(22)
                }
            }
        ]
    };
}));
const PaginationItem = /*#__PURE__*/ React.forwardRef(function PaginationItem(inProps, ref) {
    const props = useDefaultProps({
        props: inProps,
        name: 'MuiPaginationItem'
    });
    const { className, color = 'standard', component, components = {}, disabled = false, page, selected = false, shape = 'circular', size = 'medium', slots = {}, slotProps = {}, type = 'page', variant = 'text', ...other } = props;
    const ownerState = {
        ...props,
        color,
        disabled,
        selected,
        shape,
        size,
        type,
        variant
    };
    const isRtl = useRtl();
    const classes = useUtilityClasses(ownerState);
    var _slots_previous, _slots_next, _slots_first, _slots_last;
    const externalForwardedProps = {
        slots: {
            previous: (_slots_previous = slots.previous) !== null && _slots_previous !== void 0 ? _slots_previous : components.previous,
            next: (_slots_next = slots.next) !== null && _slots_next !== void 0 ? _slots_next : components.next,
            first: (_slots_first = slots.first) !== null && _slots_first !== void 0 ? _slots_first : components.first,
            last: (_slots_last = slots.last) !== null && _slots_last !== void 0 ? _slots_last : components.last
        },
        slotProps
    };
    const [PreviousSlot, previousSlotProps] = useSlot('previous', {
        elementType: NavigateBeforeIcon,
        externalForwardedProps,
        ownerState
    });
    const [NextSlot, nextSlotProps] = useSlot('next', {
        elementType: NavigateNextIcon,
        externalForwardedProps,
        ownerState
    });
    const [FirstSlot, firstSlotProps] = useSlot('first', {
        elementType: FirstPageIcon,
        externalForwardedProps,
        ownerState
    });
    const [LastSlot, lastSlotProps] = useSlot('last', {
        elementType: LastPageIcon,
        externalForwardedProps,
        ownerState
    });
    const rtlAwareType = isRtl ? ({
        previous: 'next',
        next: 'previous',
        first: 'last',
        last: 'first'
    })[type] : type;
    const IconSlot = {
        previous: PreviousSlot,
        next: NextSlot,
        first: FirstSlot,
        last: LastSlot
    }[rtlAwareType];
    const iconSlotProps = {
        previous: previousSlotProps,
        next: nextSlotProps,
        first: firstSlotProps,
        last: lastSlotProps
    }[rtlAwareType];
    return type === 'start-ellipsis' || type === 'end-ellipsis' ? /*#__PURE__*/ _jsx(PaginationItemEllipsis, {
        ref: ref,
        ownerState: ownerState,
        className: clsx(classes.root, className),
        children: "\u2026"
    }) : /*#__PURE__*/ _jsxs(PaginationItemPage, {
        ref: ref,
        ownerState: ownerState,
        component: component,
        disabled: disabled,
        className: clsx(classes.root, className),
        ...other,
        children: [
            type === 'page' && page,
            IconSlot ? /*#__PURE__*/ _jsx(PaginationItemPageIcon, {
                ...iconSlotProps,
                className: classes.icon,
                as: IconSlot
            }) : null
        ]
    });
});
"production" !== "production" ? PaginationItem.propTypes = {
    // ┌────────────────────────────── Warning ──────────────────────────────┐
    // │ These PropTypes are generated from the TypeScript type definitions. │
    // │    To update them, edit the d.ts file and run `pnpm proptypes`.     │
    // └─────────────────────────────────────────────────────────────────────┘
    /**
   * @ignore
   */ children: PropTypes.node,
    /**
   * Override or extend the styles applied to the component.
   */ classes: PropTypes.object,
    /**
   * @ignore
   */ className: PropTypes.string,
    /**
   * The active color.
   * It supports both default and custom theme colors, which can be added as shown in the
   * [palette customization guide](https://mui.com/material-ui/customization/palette/#custom-colors).
   * @default 'standard'
   */ color: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'primary',
            'secondary',
            'standard'
        ]),
        PropTypes.string
    ]),
    /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */ component: PropTypes.elementType,
    /**
   * The components used for each slot inside.
   *
   * This prop is an alias for the `slots` prop.
   * It's recommended to use the `slots` prop instead.
   *
   * @default {}
   * @deprecated use the `slots` prop instead. This prop will be removed in v7. See [Migrating from deprecated APIs](https://mui.com/material-ui/migration/migrating-from-deprecated-apis/) for more details.
   */ components: PropTypes.shape({
        first: PropTypes.elementType,
        last: PropTypes.elementType,
        next: PropTypes.elementType,
        previous: PropTypes.elementType
    }),
    /**
   * If `true`, the component is disabled.
   * @default false
   */ disabled: PropTypes.bool,
    /**
   * The current page number.
   */ page: PropTypes.node,
    /**
   * If `true` the pagination item is selected.
   * @default false
   */ selected: PropTypes.bool,
    /**
   * The shape of the pagination item.
   * @default 'circular'
   */ shape: PropTypes.oneOf([
        'circular',
        'rounded'
    ]),
    /**
   * The size of the component.
   * @default 'medium'
   */ size: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'small',
            'medium',
            'large'
        ]),
        PropTypes.string
    ]),
    /**
   * The props used for each slot inside.
   * @default {}
   */ slotProps: PropTypes.shape({
        first: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        last: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        next: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        previous: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ])
    }),
    /**
   * The components used for each slot inside.
   * @default {}
   */ slots: PropTypes.shape({
        first: PropTypes.elementType,
        last: PropTypes.elementType,
        next: PropTypes.elementType,
        previous: PropTypes.elementType
    }),
    /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */ sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object,
            PropTypes.bool
        ])),
        PropTypes.func,
        PropTypes.object
    ]),
    /**
   * The type of pagination item.
   * @default 'page'
   */ type: PropTypes.oneOf([
        'end-ellipsis',
        'first',
        'last',
        'next',
        'page',
        'previous',
        'start-ellipsis'
    ]),
    /**
   * The variant to use.
   * @default 'text'
   */ variant: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'outlined',
            'text'
        ]),
        PropTypes.string
    ])
} : void 0;
export default PaginationItem;
